import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  IonPage,
  IonContent,
  IonIcon,
  IonSpinner,
  useIonRouter,
  useIonToast,
} from '@ionic/react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useParams } from 'react-router-dom';
import {
  addOutline,
  closeOutline,
  personOutline,
  peopleOutline,
  locationOutline,
  searchOutline,
} from 'ionicons/icons';
import { useFamily, useCreateFamily, useUpdateFamily } from '@/hooks/useFamilies';
import { useCreateMember, useUpdateMember } from '@/hooks/useMembers';
import { useQueryClient } from '@tanstack/react-query';
import { useSectors } from '@/hooks/useSectors';
import { matchBlock, type BlockMatch } from '@/api/families';
import { getMembers, checkDuplicates, type DuplicateCandidate } from '@/api/members';
import { Gender, MembershipType, ChurchStatus, CivilStatus } from '@/types/member';
import type { Member, FamilyRole } from '@/types/member';
import { GENDER_LABELS, FAMILY_ROLE_LABELS } from '@/utils/labels';
import { FormPage, FormTopbar, FormSubmitRow } from '@/components/form';
import AddressAutocomplete from '@/components/shared/AddressAutocomplete';
import type { ParsedAddress } from '@/components/shared/AddressAutocomplete';

/* ─── Member Autocomplete ─── */
interface MemberAutocompleteProps {
  value: string;
  hasError?: boolean;
  onChange: (value: string) => void;
  onSelect: (member: Member) => void;
  onClear: () => void;
  selectedId: string | null | undefined;
}

const MemberAutocomplete: React.FC<MemberAutocompleteProps> = ({
  value, hasError, onChange, onSelect, onClear, selectedId,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Member[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [browseMode, setBrowseMode] = useState(false); // true → showing "no family" list
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const search = useCallback(async (q: string) => {
    if (q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await getMembers({ search: q, page_size: 8 });
      setResults(res.data);
    } catch { setResults([]); }
    setLoading(false);
  }, []);

  const loadNoFamily = useCallback(async () => {
    setLoading(true);
    setBrowseMode(true);
    try {
      const res = await getMembers({ no_family: true, page_size: 50, ordering: 'last_name' });
      setResults(res.data);
    } catch { setResults([]); }
    setLoading(false);
  }, []);

  const handleInputChange = (val: string) => {
    onChange(val);
    setQuery(val);
    setBrowseMode(false);
    if (selectedId) onClear();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 300);
    setShowDropdown(true);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <input
          className={`form-input${hasError ? ' form-input--error' : ''}`}
          value={value}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => { if (results.length > 0 && !selectedId) setShowDropdown(true); }}
          placeholder="Caută sau scrie prenume..."
          style={selectedId ? { paddingRight: 28, background: '#f0fdf4', borderColor: '#86efac' } : { paddingRight: 28 }}
        />
        <IonIcon icon={searchOutline} style={{
          position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
          fontSize: 14, color: selectedId ? '#059669' : '#94a3b8', pointerEvents: 'none',
        }} />
      </div>
      {selectedId && (
        <div style={{ fontSize: 10, color: '#059669', marginTop: 2, fontWeight: 600 }}>
          Persoană existentă selectată
        </div>
      )}
      {!selectedId && !browseMode && query.length < 2 && (
        <button
          type="button"
          onClick={loadNoFamily}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            marginTop: 4, padding: '3px 8px',
            background: 'rgba(79,70,229,0.08)',
            border: '1px dashed rgba(79,70,229,0.32)',
            borderRadius: 999, color: '#4f46e5',
            fontSize: 11, fontWeight: 600, letterSpacing: '0.01em',
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          <IonIcon icon={personOutline} style={{ fontSize: 12 }} />
          Alege din persoane fără familie
        </button>
      )}
      {showDropdown && !selectedId && (results.length > 0 || loading || browseMode) && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: 280, overflowY: 'auto',
          marginTop: 4,
        }}>
          {browseMode && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '7px 12px', background: '#f8fafc',
              borderBottom: '1px solid #e2e8f0', fontSize: 11, fontWeight: 700,
              letterSpacing: '0.06em', textTransform: 'uppercase', color: '#4f46e5',
            }}>
              <span>Persoane fără familie ({results.length})</span>
              <button
                type="button"
                onClick={() => { setBrowseMode(false); setResults([]); setShowDropdown(false); }}
                style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 13, padding: 0, lineHeight: 1 }}
              >
                <IonIcon icon={closeOutline} />
              </button>
            </div>
          )}
          {loading && (
            <div style={{ padding: '8px 12px', fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
              Se caută...
            </div>
          )}
          {!loading && browseMode && results.length === 0 && (
            <div style={{ padding: '10px 12px', fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
              Nu există persoane fără familie.
            </div>
          )}
          {!loading && results.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => { onSelect(m); setShowDropdown(false); setResults([]); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                padding: '8px 12px', border: 'none', background: 'none',
                cursor: 'pointer', textAlign: 'left', fontSize: 13,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#f8fafc')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
            >
              <div style={{
                width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                background: '#5e81f4', color: '#fff', fontSize: 12, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {(m.first_name[0] ?? '').toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: '#1c1d21' }}>
                  {m.last_name} {m.first_name}
                </div>
                <div style={{ fontSize: 11, color: '#8181a5' }}>
                  {m.age != null ? `${m.age} ani` : ''}
                  {m.family_name ? ` · Fam. ${m.family_name}` : ' · Fără familie'}
                  {m.sector_name ? ` · ${m.sector_name}` : ''}
                </div>
              </div>
            </button>
          ))}
          {!loading && results.length === 0 && query.length >= 2 && (
            <div style={{ padding: '8px 12px', fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
              Niciun rezultat. Se va crea persoană nouă.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Inline duplicate warning ───
   Self-contained: debounced name lookup for a single new (non-linked) member
   row. Mirrors the soft, non-blocking warning on the standalone member editor
   so duplicate aparținători (and members) are surfaced when added inline. */
interface InlineDuplicateWarningProps {
  firstName: string;
  lastName: string;
  existingMemberId: string | null | undefined;
}

const InlineDuplicateWarning: React.FC<InlineDuplicateWarningProps> = ({
  firstName, lastName, existingMemberId,
}) => {
  const [candidates, setCandidates] = useState<DuplicateCandidate[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    // Linked to an existing person → not a duplicate; nothing to warn about.
    if (existingMemberId || (firstName?.length ?? 0) < 2 || (lastName?.length ?? 0) < 2) {
      setCandidates([]);
      return;
    }
    timerRef.current = setTimeout(async () => {
      try {
        const results = await checkDuplicates({ first_name: firstName, last_name: lastName });
        setCandidates(results);
      } catch { /* ignore */ }
    }, 500);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [firstName, lastName, existingMemberId]);

  if (candidates.length === 0) return null;

  return (
    <div className="duplicate-warning" style={{ margin: '8px 0 0' }}>
      <strong>Atenție!</strong> Persoane cu nume similar există deja:
      <ul>
        {candidates.map((d) => (
          <li key={d.id}>
            {d.last_name} {d.first_name}
            {d.membership_type ? ` · ${d.membership_type === 'dependent' ? 'aparținător' : 'membru'}` : ''}
            {d.birth_date ? ` · ${new Date(d.birth_date).toLocaleDateString('ro-RO')}` : ''}
          </li>
        ))}
      </ul>
    </div>
  );
};

/* ─── Schemas ─── */
const inlineMemberSchema = z.object({
  first_name: z.string().min(1, 'Obligatoriu'),
  last_name: z.string().min(1, 'Obligatoriu'),
  gender: z.nativeEnum(Gender),
  family_role: z.enum(['head', 'spouse', 'child', 'other'] as const),
  birth_date: z.string().nullable().optional(),
  cell_phone: z.string().nullable().optional(),
  membership_type: z.nativeEnum(MembershipType),
  existing_member_id: z.string().nullable().optional(),
});

const familyFormSchema = z.object({
  name: z.string().min(1, 'Numele familiei este obligatoriu'),
  address: z.string().min(1, 'Adresa este obligatorie'),
  numar: z.string().nullable().optional(),
  bloc: z.string().nullable().optional(),
  scara: z.string().nullable().optional(),
  etaj: z.string().nullable().optional(),
  apartament: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  wedding_date: z.string().nullable().optional(),
  sector_id: z.string().nullable().optional(),
  has_unbelieving_spouse: z.boolean().optional(),
  // Form-only: a widow/widower heads the household alone. Drops the spouse
  // row and records the head's civil status; never sent to the family API.
  widowed: z.boolean().optional(),
  notes: z.string().nullable().optional(),
  members: z.array(inlineMemberSchema),
});

type FamilyFormValues = z.infer<typeof familyFormSchema>;
type InlineMember = z.infer<typeof inlineMemberSchema>;

function getDefaultRow(familyName: string, role: FamilyRole): InlineMember {
  return {
    first_name: '', last_name: familyName,
    gender: role === 'spouse' ? Gender.FEMALE : Gender.MALE,
    family_role: role,
    birth_date: null, cell_phone: null,
    membership_type: role === 'child' ? MembershipType.DEPENDENT : MembershipType.MEMBER,
    existing_member_id: null,
  };
}

/* ─── Component ─── */
const FamilyEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const router = useIonRouter();
  const [present] = useIonToast();
  const isEditing = !!id && id !== 'new';
  const { data: existingFamily, isLoading } = useFamily(isEditing ? id : undefined);
  const { data: sectors } = useSectors();
  const queryClient = useQueryClient();
  const createFamilyMut = useCreateFamily();
  const updateFamilyMut = useUpdateFamily();
  const createMemberMut = useCreateMember();
  const updateMemberMut = useUpdateMember();
  const [isSaving, setIsSaving] = useState(false);

  const { control, handleSubmit, reset, watch, setValue, formState: { errors, isDirty } } = useForm<FamilyFormValues>({
    resolver: zodResolver(familyFormSchema),
    defaultValues: {
      name: '', address: '', numar: null, bloc: null, scara: null, etaj: null, apartament: null, city: '', latitude: null, longitude: null,
      wedding_date: null, sector_id: null, has_unbelieving_spouse: false, widowed: false, notes: null,
      members: [getDefaultRow('', 'head'), getDefaultRow('', 'spouse')],
    },
  });

  // Block matching state
  const [blockMatches, setBlockMatches] = useState<BlockMatch[]>([]);
  const blockMatchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { fields, append, remove } = useFieldArray({ control, name: 'members' });
  const watchName = watch('name');
  const watchWidowed = !!watch('widowed');

  // Widow/widower: the household has no spouse row. Turning the flag on
  // removes any spouse rows; turning it off gives the default spouse row back.
  const setWidowed = (on: boolean) => {
    setValue('widowed', on, { shouldDirty: true });
    const roles = fields.map((_, i) => watch(`members.${i}.family_role`));
    if (on) {
      const spouseIdx = roles.map((r, i) => (r === 'spouse' ? i : -1)).filter((i) => i >= 0);
      if (spouseIdx.length) remove(spouseIdx);
    } else if (!roles.includes('spouse')) {
      append(getDefaultRow(watchName || '', 'spouse'));
    }
  };

  useEffect(() => {
    if (existingFamily) {
      reset({
        name: existingFamily.name, address: existingFamily.address ?? '',
        numar: existingFamily.numar ?? null,
        bloc: existingFamily.bloc ?? null, scara: existingFamily.scara ?? null,
        etaj: existingFamily.etaj ?? null, apartament: existingFamily.apartament ?? null,
        city: existingFamily.city ?? '', latitude: existingFamily.latitude, longitude: existingFamily.longitude,
        wedding_date: existingFamily.wedding_date,
        sector_id: existingFamily.sector_id, has_unbelieving_spouse: existingFamily.has_unbelieving_spouse,
        notes: existingFamily.notes, members: [],
      });
    }
  }, [existingFamily, reset]);

  // Block matching: when bloc field changes, search for matching families
  const watchBloc = watch('bloc');
  const watchAddress = watch('address');
  useEffect(() => {
    if (blockMatchDebounce.current) clearTimeout(blockMatchDebounce.current);
    if (!watchBloc?.trim()) {
      setBlockMatches([]);
      return;
    }
    blockMatchDebounce.current = setTimeout(async () => {
      try {
        const matches = await matchBlock(watchBloc.trim(), watchAddress || undefined);
        // Exclude current family if editing
        setBlockMatches(isEditing ? matches.filter(m => m.id !== id) : matches);
      } catch {
        setBlockMatches([]);
      }
    }, 500);
    return () => { if (blockMatchDebounce.current) clearTimeout(blockMatchDebounce.current); };
  }, [watchBloc, watchAddress, isEditing, id]);

  const onSubmit = async (data: FamilyFormValues) => {
    setIsSaving(true);
    try {
      const payload = {
        name: data.name, address: data.address || null,
        numar: data.numar || null,
        bloc: data.bloc || null, scara: data.scara || null,
        etaj: data.etaj || null, apartament: data.apartament || null,
        city: data.city || null,
        latitude: data.latitude ?? null, longitude: data.longitude ?? null,
        wedding_date: data.wedding_date || null, sector_id: data.sector_id || null,
        has_unbelieving_spouse: data.has_unbelieving_spouse ?? false, notes: data.notes || null,
      };

      if (isEditing) {
        await updateFamilyMut.mutateAsync({ id, data: payload });
        for (const m of data.members) {
          if (m.existing_member_id) {
            await updateMemberMut.mutateAsync({ id: m.existing_member_id, data: {
              family_id: id,
              first_name: m.first_name, last_name: m.last_name, gender: m.gender,
              family_role: m.family_role, birth_date: m.birth_date || null,
              cell_phone: m.cell_phone || null, membership_type: m.membership_type,
            }});
          } else if (m.first_name.trim()) {
            await createMemberMut.mutateAsync({
              first_name: m.first_name, last_name: m.last_name, gender: m.gender,
              family_id: id, family_role: m.family_role, birth_date: m.birth_date || null,
              cell_phone: m.cell_phone || null, membership_type: m.membership_type,
              church_status: ChurchStatus.ACTIVE, sector_id: existingFamily?.sector_id || data.sector_id || null,
            });
          }
        }
        await queryClient.invalidateQueries({ queryKey: ['families', 'detail', id] });
        await queryClient.invalidateQueries({ queryKey: ['families', 'list'] });
        await queryClient.invalidateQueries({ queryKey: ['members'] });
        present({ message: 'Familia a fost actualizata.', duration: 2000, color: 'success', position: 'bottom' });
        router.goBack();
      } else {
        const fam = await createFamilyMut.mutateAsync(payload);
        const memberRows = data.members.filter((m) => m.first_name.trim());
        for (const m of memberRows) {
          const widowedHead = data.widowed && m.family_role === 'head' ? { civil_status: CivilStatus.WIDOWED } : {};
          if (m.existing_member_id) {
            // Link existing member to this family
            await updateMemberMut.mutateAsync({ id: m.existing_member_id, data: {
              family_id: fam.id, family_role: m.family_role,
              first_name: m.first_name, last_name: m.last_name, gender: m.gender,
              birth_date: m.birth_date || null, cell_phone: m.cell_phone || null,
              membership_type: m.membership_type, ...widowedHead,
            }});
          } else {
            await createMemberMut.mutateAsync({
              first_name: m.first_name, last_name: m.last_name, gender: m.gender,
              family_id: fam.id, family_role: m.family_role, birth_date: m.birth_date || null,
              cell_phone: m.cell_phone || null, membership_type: m.membership_type,
              church_status: ChurchStatus.ACTIVE, sector_id: data.sector_id || null, ...widowedHead,
            });
          }
        }
        await queryClient.invalidateQueries({ queryKey: ['families'] });
        await queryClient.invalidateQueries({ queryKey: ['members'] });
        present({ message: `Familia ${fam.name} creata cu ${memberRows.length} membri.`, duration: 2000, color: 'success', position: 'bottom' });
        router.push(`/families/${fam.id}`, 'forward', 'replace');
      }
    } catch {
      present({ message: 'Eroare la salvare.', duration: 3000, color: 'danger', position: 'bottom' });
    } finally { setIsSaving(false); }
  };

  const addRow = () => {
    const roles = fields.map((_, i) => watch(`members.${i}.family_role`));
    let role: FamilyRole = 'child';
    if (!roles.includes('head')) role = 'head';
    else if (!roles.includes('spouse') && !watchWidowed) role = 'spouse';
    append(getDefaultRow(watchName || '', role));
  };

  if (isEditing && isLoading) {
    return <IonPage><IonContent><div className="form-loading"><IonSpinner name="crescent" /></div></IonContent></IonPage>;
  }

  return (
    <IonPage>
      <IonContent>
        <FormPage>
          <FormTopbar
            title={isEditing ? 'Editare Familie' : 'Familie Nouă'}
            icon={peopleOutline}
            onBack={() => router.goBack()}
            onSave={handleSubmit(onSubmit)}
            saving={isSaving}
            disabled={!isDirty && isEditing}
          />

          <form onSubmit={handleSubmit(onSubmit)}>

            {/* ═══ DATE FAMILIE ═══ */}
            <div className="form-section">
              <div className="form-section__header"><h3 className="form-section__title">Date Familie</h3></div>
              <div className="form-section__body">
                <div className="form-grid form-grid--3">
                  <div className="form-field">
                    <label className="form-field__label">Numele familiei<span className="form-field__req">*</span></label>
                    <Controller control={control} name="name"
                      render={({ field }) => (
                        <input className="form-input" value={field.value} placeholder="ex: Popescu"
                          onChange={(e) => {
                            const v = e.target.value; field.onChange(v);
                            if (!isEditing) fields.forEach((_, i) => {
                              const cur = watch(`members.${i}.last_name`);
                              if (!cur || cur === watchName || cur === '') setValue(`members.${i}.last_name`, v);
                            });
                          }} />
                      )} />
                    {errors.name && <span className="form-field__error">{errors.name.message}</span>}
                  </div>
                  <div className="form-field form-field--span-2">
                    <label className="form-field__label">Adresa<span className="form-field__req">*</span></label>
                    <AddressAutocomplete
                      value={[watch('address'), watch('city')].filter(Boolean).join(', ')}
                      placeholder="Scrie adresa sau cauta..."
                      hasError={!!(errors.address || errors.city)}
                      onSelect={(result: ParsedAddress) => {
                        setValue('address', result.address || result.displayName, { shouldDirty: true, shouldValidate: true });
                        setValue('city', result.city || '', { shouldDirty: true, shouldValidate: true });
                        setValue('latitude', result.latitude, { shouldDirty: true });
                        setValue('longitude', result.longitude, { shouldDirty: true });
                      }}
                      onClear={() => {
                        setValue('address', '', { shouldDirty: true, shouldValidate: true });
                        setValue('city', '', { shouldDirty: true, shouldValidate: true });
                        setValue('latitude', null, { shouldDirty: true });
                        setValue('longitude', null, { shouldDirty: true });
                      }}
                      onManualChange={(val) => {
                        setValue('address', val, { shouldDirty: true, shouldValidate: true });
                      }}
                    />
                    {errors.address && <span className="form-field__error">{errors.address.message}</span>}
                  </div>
                </div>

                {/* ── Detalii adresă (Nr, Bloc, Scara, Etaj, Ap) ── */}
                <div className="form-grid form-grid--6" style={{ marginTop: 12 }}>
                  <div className="form-field">
                    <label className="form-field__label">Nr.</label>
                    <Controller control={control} name="numar"
                      render={({ field }) => (
                        <input className="form-input" value={field.value ?? ''} placeholder="15"
                          onChange={(e) => field.onChange(e.target.value || null)} />
                      )} />
                  </div>
                  <div className="form-field">
                    <label className="form-field__label">Bloc</label>
                    <Controller control={control} name="bloc"
                      render={({ field }) => (
                        <input className="form-input" value={field.value ?? ''} placeholder="B2"
                          onChange={(e) => field.onChange(e.target.value || null)} />
                      )} />
                  </div>
                  <div className="form-field">
                    <label className="form-field__label">Scara</label>
                    <Controller control={control} name="scara"
                      render={({ field }) => (
                        <input className="form-input" value={field.value ?? ''} placeholder="A"
                          onChange={(e) => field.onChange(e.target.value || null)} />
                      )} />
                  </div>
                  <div className="form-field">
                    <label className="form-field__label">Etaj</label>
                    <Controller control={control} name="etaj"
                      render={({ field }) => (
                        <input className="form-input" value={field.value ?? ''} placeholder="3"
                          onChange={(e) => field.onChange(e.target.value || null)} />
                      )} />
                  </div>
                  <div className="form-field">
                    <label className="form-field__label">Ap.</label>
                    <Controller control={control} name="apartament"
                      render={({ field }) => (
                        <input className="form-input" value={field.value ?? ''} placeholder="12"
                          onChange={(e) => field.onChange(e.target.value || null)} />
                      )} />
                  </div>
                  <div className="form-field">
                    <label className="form-field__label">Oraș</label>
                    <Controller control={control} name="city"
                      render={({ field }) => (
                        <input className="form-input" value={field.value ?? ''} placeholder="Arad"
                          onChange={(e) => field.onChange(e.target.value || null)} />
                      )} />
                  </div>
                </div>

                {/* Block matching suggestion */}
                {blockMatches.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div className="block-match-hint">
                        <IonIcon icon={locationOutline} style={{ fontSize: 16, color: '#5e81f4', flexShrink: 0 }} />
                        <div className="block-match-hint__content">
                          <span className="block-match-hint__title">
                            {blockMatches.length === 1 ? 'O familie' : `${blockMatches.length} familii`} la același bloc
                          </span>
                          {blockMatches.map((m) => (
                            <button
                              key={m.id}
                              type="button"
                              className="block-match-hint__btn"
                              onClick={() => {
                                setValue('latitude', m.latitude, { shouldDirty: true });
                                setValue('longitude', m.longitude, { shouldDirty: true });
                                if (m.city && !watch('city')) setValue('city', m.city, { shouldDirty: true });
                                present({ message: `Coordonate preluate de la familia ${m.name}`, duration: 2000, color: 'success', position: 'bottom' });
                                setBlockMatches([]);
                              }}
                            >
                              Folosește locația familiei {m.name}
                              {m.scara ? ` (Sc. ${m.scara})` : ''}
                            </button>
                          ))}
                        </div>
                      </div>
                  </div>
                )}

                <div className="form-grid form-grid--3" style={{ marginTop: 12 }}>
                  <div className="form-field">
                    <label className="form-field__label">Data căsătoriei</label>
                    <Controller control={control} name="wedding_date"
                      render={({ field }) => <input className="form-input" type="date" value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value)} />} />
                  </div>
                  <div className="form-field">
                    <label className="form-field__label">Sector</label>
                    <Controller control={control} name="sector_id"
                      render={({ field }) => (
                        <select className="form-input form-select" value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value || null)}>
                          <option value="">Nealocat</option>
                          {(sectors ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                      )} />
                  </div>
                  <div className="form-field" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 4 }}>
                    <Controller control={control} name="has_unbelieving_spouse"
                      render={({ field }) => (
                        <label className="form-toggle">
                          <div className={`form-toggle__track${field.value ? ' form-toggle__track--on' : ''}`} onClick={() => field.onChange(!field.value)}>
                            <div className="form-toggle__thumb" />
                          </div>
                          <span className="form-toggle__label">Soț/soție necredincios(ă)</span>
                        </label>
                      )} />
                  </div>
                  {!isEditing && (
                    <div className="form-field" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 4 }}>
                      <label className="form-toggle">
                        <div className={`form-toggle__track${watchWidowed ? ' form-toggle__track--on' : ''}`} onClick={() => setWidowed(!watchWidowed)}>
                          <div className="form-toggle__thumb" />
                        </div>
                        <span className="form-toggle__label">Văduv(ă) — fără soț/soție</span>
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ═══ EXISTING MEMBERS (edit) ═══ */}
            {isEditing && existingFamily && existingFamily.members.length > 0 && (
              <div className="form-section">
                <div className="form-section__header">
                  <h3 className="form-section__title">Membri Existenți ({existingFamily.members.length})</h3>
                </div>
                <div className="form-section__body">
                  <div className="form-member-grid">
                    {[...existingFamily.members]
                      .sort((a, b) => {
                        const o: Record<string, number> = { head: 0, spouse: 1, child: 2 };
                        return (o[a.family_role ?? 'child'] ?? 9) - (o[b.family_role ?? 'child'] ?? 9);
                      })
                      .map((m) => (
                        <div key={m.id} className="form-member-card" onClick={() => router.push(`/members/${m.id}/edit`)}>
                          <div className="form-member-card__avatar" style={{
                            background: m.family_role === 'head' ? '#5e81f4' : m.family_role === 'spouse' ? '#9698d6' : '#7ce7ac',
                          }}>
                            {(m.first_name[0] ?? '').toUpperCase()}
                          </div>
                          <div className="form-member-card__info">
                            <div className="form-member-card__name">{m.full_name}</div>
                            <div className="form-member-card__role">
                              {FAMILY_ROLE_LABELS[m.family_role ?? ''] ?? 'Membru'}
                              {m.age != null && ` · ${m.age} ani`}
                            </div>
                          </div>
                          <span className="form-member-card__action">Editează →</span>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            )}

            {/* ═══ INLINE MEMBER ROWS ═══ */}
            <div className="form-section">
              <div className="form-section__header">
                <h3 className="form-section__title">{isEditing ? 'Adaugă Membri Noi' : 'Membrii Familiei'}</h3>
                <button type="button" className="form-btn form-btn--small" onClick={addRow}>
                  <IonIcon icon={addOutline} /> Adaugă
                </button>
              </div>
              <div className="form-section__body">
                {fields.length === 0 && (
                  <div className="form-empty">
                    <IonIcon icon={personOutline} />
                    <span>Apasă "Adaugă" pentru a adăuga membri.</span>
                  </div>
                )}

                {fields.map((field, idx) => {
                  const mErrors = errors.members?.[idx];
                  const role = watch(`members.${idx}.family_role`);
                  const roleColor = role === 'head' ? '#5e81f4' : role === 'spouse' ? '#9698d6' : '#7ce7ac';

                  return (
                    <div key={field.id} className="form-member-row">
                      <div className="form-member-row__header">
                        <div className="form-member-row__badge" style={{ background: roleColor }} />
                        <span className="form-member-row__title">{FAMILY_ROLE_LABELS[role] ?? 'Membru'}</span>
                        <button type="button" className="form-member-row__remove" onClick={() => remove(idx)}>
                          <IonIcon icon={closeOutline} />
                        </button>
                      </div>
                      <div className="form-grid form-grid--6">
                        <div className="form-field form-field--span-2">
                          <label className="form-field__label">Prenume *</label>
                          <Controller control={control} name={`members.${idx}.first_name`}
                            render={({ field: f }) => (
                              <MemberAutocomplete
                                value={f.value}
                                hasError={!!mErrors?.first_name}
                                onChange={(val) => f.onChange(val)}
                                selectedId={watch(`members.${idx}.existing_member_id`)}
                                onClear={() => setValue(`members.${idx}.existing_member_id`, null)}
                                onSelect={(m) => {
                                  setValue(`members.${idx}.first_name`, m.first_name, { shouldDirty: true });
                                  setValue(`members.${idx}.last_name`, m.last_name, { shouldDirty: true });
                                  setValue(`members.${idx}.gender`, m.gender, { shouldDirty: true });
                                  setValue(`members.${idx}.birth_date`, m.birth_date ?? null, { shouldDirty: true });
                                  setValue(`members.${idx}.existing_member_id`, m.id, { shouldDirty: true });
                                  if (m.membership_type) setValue(`members.${idx}.membership_type`, m.membership_type, { shouldDirty: true });

                                  // Auto-fill family address from selected member if family address is empty
                                  const memberAddress = m.address ?? m.effective_address ?? null;
                                  if (memberAddress && !watch('address')) {
                                    setValue('address', memberAddress, { shouldDirty: true, shouldValidate: true });
                                    setValue('numar', m.numar ?? m.effective_numar ?? null, { shouldDirty: true });
                                    setValue('bloc', m.bloc ?? m.effective_bloc ?? null, { shouldDirty: true });
                                    setValue('scara', m.scara ?? m.effective_scara ?? null, { shouldDirty: true });
                                    setValue('etaj', m.etaj ?? m.effective_etaj ?? null, { shouldDirty: true });
                                    setValue('apartament', m.apartament ?? m.effective_apartament ?? null, { shouldDirty: true });
                                    setValue('city', m.city ?? m.effective_city ?? '', { shouldDirty: true });
                                    setValue('latitude', m.latitude ?? m.effective_latitude ?? null, { shouldDirty: true });
                                    setValue('longitude', m.longitude ?? m.effective_longitude ?? null, { shouldDirty: true });
                                  }
                                }}
                              />
                            )} />
                        </div>
                        <div className="form-field">
                          <label className="form-field__label">Nume *</label>
                          <Controller control={control} name={`members.${idx}.last_name`}
                            render={({ field: f }) => <input className="form-input" value={f.value} onChange={(e) => f.onChange(e.target.value)} />} />
                        </div>
                        <div className="form-field">
                          <label className="form-field__label">Rol</label>
                          <Controller control={control} name={`members.${idx}.family_role`}
                            render={({ field: f }) => (
                              <select className="form-input form-select" value={f.value} onChange={(e) => {
                                const r = e.target.value as FamilyRole; f.onChange(r);
                                if (r === 'spouse') { setValue(`members.${idx}.gender`, Gender.FEMALE); setValue(`members.${idx}.membership_type`, MembershipType.MEMBER); }
                                else if (r === 'head') { setValue(`members.${idx}.gender`, Gender.MALE); setValue(`members.${idx}.membership_type`, MembershipType.MEMBER); }
                                else if (r === 'child') setValue(`members.${idx}.membership_type`, MembershipType.DEPENDENT);
                              }}>
                                {(['head', 'spouse', 'child', 'other'] as FamilyRole[]).map((r) => <option key={r} value={r}>{FAMILY_ROLE_LABELS[r]}</option>)}
                              </select>
                            )} />
                        </div>
                        <div className="form-field">
                          <label className="form-field__label">Gen</label>
                          <Controller control={control} name={`members.${idx}.gender`}
                            render={({ field: f }) => (
                              <select className="form-input form-select" value={f.value} onChange={(e) => f.onChange(e.target.value)}>
                                {Object.values(Gender).map((g) => <option key={g} value={g}>{GENDER_LABELS[g]}</option>)}
                              </select>
                            )} />
                        </div>
                        <div className="form-field">
                          <label className="form-field__label">Data nașterii</label>
                          <Controller control={control} name={`members.${idx}.birth_date`}
                            render={({ field: f }) => <input className="form-input" type="date" value={f.value ?? ''} onChange={(e) => f.onChange(e.target.value)} />} />
                        </div>
                      </div>
                      <InlineDuplicateWarning
                        firstName={watch(`members.${idx}.first_name`)}
                        lastName={watch(`members.${idx}.last_name`)}
                        existingMemberId={watch(`members.${idx}.existing_member_id`)}
                      />
                    </div>
                  );
                })}

                {!isEditing && fields.length > 0 && (
                  <div className="form-quick-add">
                    {!fields.some((_, i) => watch(`members.${i}.family_role`) === 'head') && (
                      <button type="button" className="form-btn form-btn--outline" onClick={() => append(getDefaultRow(watchName || '', 'head'))}>+ Cap Familie</button>
                    )}
                    {!watchWidowed && !fields.some((_, i) => watch(`members.${i}.family_role`) === 'spouse') && (
                      <button type="button" className="form-btn form-btn--outline" onClick={() => append(getDefaultRow(watchName || '', 'spouse'))}>+ Soț/Soție</button>
                    )}
                    <button type="button" className="form-btn form-btn--outline" onClick={() => append(getDefaultRow(watchName || '', 'child'))}>+ Copil</button>
                  </div>
                )}
              </div>
            </div>

            {/* ═══ OBSERVAȚII ═══ */}
            <div className="form-section">
              <div className="form-section__header"><h3 className="form-section__title">Observații</h3></div>
              <div className="form-section__body">
                <Controller control={control} name="notes"
                  render={({ field }) => (
                    <textarea className="form-textarea" value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value)}
                      rows={3} placeholder="Observații despre familie..." />
                  )} />
              </div>
            </div>

            <FormSubmitRow
              onCancel={() => router.goBack()}
              submitLabel={isEditing ? 'Actualizează' : `Creează Familie${fields.length > 0 ? ` cu ${fields.length} Membri` : ''}`}
              saving={isSaving}
              disabled={!isDirty && isEditing}
            />
          </form>
        </FormPage>

        {/* ── Saving overlay ── */}
        {isSaving && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(255,255,255,0.7)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, backdropFilter: 'blur(2px)',
          }}>
            <IonSpinner name="crescent" style={{ width: 36, height: 36, color: '#5e81f4' }} />
            <p style={{ marginTop: 12, fontSize: 14, fontWeight: 600, color: '#475569' }}>
              {isEditing ? 'Se actualizează...' : 'Se creează...'}
            </p>
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};

export default FamilyEditPage;
